<template>
  <div
    v-if="visible"
    class="sc-mask"
    @touchmove.prevent
  >
    <div
      class="sc-box"
      :style="{ width: boxWidth + 'px' }"
    >
      <div class="sc-head">
        <div class="sc-title">{{ passed ? "验证成功" : "请拖动滑块完成拼图" }}</div>
        <div
          class="sc-close"
          @click="onClose"
        >
          ×
        </div>
      </div>

      <!-- 拼图区域：纯图片背景 + 半透明白色磨砂缺口（参考 vue-puzzle-vcode 的视觉风格） -->
      <div
        class="sc-puzzle"
        :class="{ shaking: failShake }"
        :style="{ width: width + 'px', height: height + 'px' }"
      >
        <canvas
          ref="scMain"
          class="sc-canvas"
          :style="{ width: width + 'px', height: height + 'px' }"
        />
        <!-- 拼块画布：经典拼图块，内容为缺口处同位置图片裁切 -->
        <canvas
          v-if="!passed"
          ref="scPiece"
          class="sc-piece-canvas"
          :class="{ dragging: dragging }"
          :style="pieceCanvasStyle"
        />
        <div
          v-if="passed"
          class="sc-ok"
        >
          ✓
        </div>
      </div>

      <!-- 拖动条：白色轨道 + 蓝色进度 + 白色把手（vcode 风格） -->
      <div class="sc-bar">
        <div
          ref="scTrack"
          class="sc-track"
          :style="{ width: width + 'px' }"
          @pointerdown="onStart"
          @pointermove="onMove"
          @pointerup="onEnd"
          @pointercancel="onEnd"
        >
          <div
            class="sc-fill"
            :class="{ ok: passed }"
            :style="{ width: fillW + 'px' }"
          />
          <div class="sc-tip">{{ tipText }}</div>
          <div
            class="sc-handle"
            :class="{ dragging: dragging, ok: passed }"
            :style="{ transform: 'translateX(' + handleOffset + 'px)' }"
          >
            <div
              v-if="!passed"
              class="sc-bars"
            >
              <div class="sc-bar-line" />
              <div class="sc-bar-line" />
              <div class="sc-bar-line" />
            </div>
            <div
              v-else
              class="sc-handle-icon"
            >
              ✓
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 滑动拼图验证组件（纯前端本地校验，无后端接口）
 * 由 wmMiniApp 小程序版 slide-captcha.vue 移植到 Vue2 Web：
 * - 拼块为参数化拼图造型：3e×3e 方体（宽高一致），四条边随机分配"半圆凸起/半圆凹口/平直"（每次挑战形状不同）
 * - 缺口 = 半透明白色磨砂 + 白色描边 + 轻微内阴影
 * - Web 版默认不用图片做底：背景程序化随机生成（渐变 + 同色系装饰图形，每次挑战不同），
 *   仅在传入 image 时才走图片 cover 裁切（支持单图或数组随机取一张）
 * 通过后 $emit('success')，点击右上角关闭 $emit('close')。
 * 拖动事件使用 pointer events（鼠标/触摸统一），配合 touch-action:none。
 */

function rand(min, max) {
  return min + Math.random() * (max - min);
}
function randInt(min, max) {
  return Math.floor(rand(min, max + 1));
}

// 圆角矩形路径（自绘，兼容老浏览器无 ctx.roundRect 的情况）
function roundRectPath(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * 参数化拼图块路径（方体 3e×3e，宽高一致；四条边各有半圆特征，半径 e/2，跨度 e）：
 * f = { top, right, bottom, left }，每边 1=凸起、-1=凹口、0=平直。
 * 全部用显式 arc 画半圆，方向经几何验证（凸起朝体外、凹口朝体内）。
 * @param x,y 方体左上角（画布坐标）
 * @param e   单位长度（方体宽的 1/3）
 */
function traceBrickPath(ctx, x, y, e, f) {
  const PI = Math.PI;
  ctx.beginPath();
  ctx.moveTo(x, y);
  // 上边（左→右），特征中心 x+1.5e
  if (f.top !== 0) {
    ctx.lineTo(x + e, y);
    ctx.arc(x + 1.5 * e, y, e / 2, PI, 0, f.top === 1 ? false : true);
    ctx.lineTo(x + 3 * e, y);
  } else {
    ctx.lineTo(x + 3 * e, y);
  }
  // 右边（上→下），特征中心 y+1.5e
  if (f.right !== 0) {
    ctx.lineTo(x + 3 * e, y + e);
    ctx.arc(
      x + 3 * e,
      y + 1.5 * e,
      e / 2,
      -PI / 2,
      PI / 2,
      f.right === 1 ? false : true
    );
    ctx.lineTo(x + 3 * e, y + 3 * e);
  } else {
    ctx.lineTo(x + 3 * e, y + 3 * e);
  }
  // 下边（右→左），特征中心 x+1.5e
  if (f.bottom !== 0) {
    ctx.lineTo(x + 2 * e, y + 3 * e);
    ctx.arc(
      x + 1.5 * e,
      y + 3 * e,
      e / 2,
      0,
      PI,
      f.bottom === 1 ? false : true
    );
    ctx.lineTo(x, y + 3 * e);
  } else {
    ctx.lineTo(x, y + 3 * e);
  }
  // 左边（下→上），特征中心 y+1.5e
  if (f.left !== 0) {
    ctx.lineTo(x, y + 2 * e);
    ctx.arc(x, y + 1.5 * e, e / 2, PI / 2, -PI / 2, f.left === 1 ? false : true);
    ctx.lineTo(x, y);
  } else {
    ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export default {
  name: "SlideCaptcha",
  props: {
    visible: { type: Boolean, default: false },
    // 拼图底图：不传（null）时走程序化随机背景；可传单图路径或图片数组（随机取一张）
    image: { type: [String, Array], default: null },
    // 拼图区域宽高（px）
    width: { type: Number, default: 300 },
    height: { type: Number, default: 150 },
    // 允许误差（px，画布坐标系）
    tolerance: { type: Number, default: 8 },
  },
  data() {
    return {
      W: 0, // 主画布实际宽高（px）
      H: 0,
      e: 0, // 拼块单位长度（px）
      shape: null, // 拼块形状 { top, right, bottom, left }: 1凸 -1凹 0平
      holeX: 0, // 缺口方体左上角（画布坐标）
      holeY: 0,
      offset: 0, // 拼块方体当前 x（画布坐标）
      offMin: 0, // 可拖动范围
      offMax: 0,
      pieceLeft: 0, // 拼块画布 CSS 定位（px）
      pieceTop: 0,
      pieceCssW: 0,
      pieceCssH: 0,
      dragging: false,
      passed: false,
      verifying: false, // 通过判定中（吸附动画期间禁止再拖）
      failShake: false,
      ready: false,
      tipText: "按住滑块，拖动完成拼图",
      startX: 0,
      baseOffset: 0,
      handlePx: 44, // 滑块把手实际宽度（px）
      pieceMargin: 4, // 拼块画布描边/投影留白
      bgParams: null,
      img: null,
      dpr: 2,
      _failTimer: null,
      _passTimer: null,
      _emitTimer: null,
      _docListeners: false,
    };
  },
  computed: {
    boxWidth() {
      return this.width + 32;
    },
    pieceCanvasStyle() {
      if (!this.ready) {
        // 初始化完成前藏到画面外，避免拼块从左上角闪现
        return { left: "-9999px", top: "-9999px", width: "1px", height: "1px" };
      }
      return {
        left: this.pieceLeft + "px",
        top: this.pieceTop + "px",
        width: this.pieceCssW + "px",
        height: this.pieceCssH + "px",
      };
    },
    handleOffset() {
      const range = this.offMax - this.offMin;
      if (!range || !this.W) return 0;
      return (
        ((this.offset - this.offMin) / range) *
        Math.max(this.W - this.handlePx, 0)
      );
    },
    fillW() {
      return this.handleOffset + this.handlePx;
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.reset();
        this.$nextTick(() => {
          this.initCanvas();
        });
      } else {
        this.clearTimers();
      }
    },
  },
  created() {
    this.dpr = window.devicePixelRatio || 2;
  },
  beforeDestroy() {
    this.clearTimers();
    this.removeDocListeners();
  },
  methods: {
    clearTimers() {
      if (this._failTimer) {
        clearTimeout(this._failTimer);
        this._failTimer = null;
      }
      if (this._passTimer) {
        clearTimeout(this._passTimer);
        this._passTimer = null;
      }
      if (this._emitTimer) {
        clearTimeout(this._emitTimer);
        this._emitTimer = null;
      }
    },

    removeDocListeners() {
      if (this._docListeners) {
        this._docListeners = false;
        document.removeEventListener("mousemove", this.onMove);
        document.removeEventListener("mouseup", this.onEnd);
      }
    },

    reset() {
      this.passed = false;
      this.dragging = false;
      this.verifying = false;
      this.failShake = false;
      this.ready = false;
      this.offset = 0;
      this.tipText = "按住滑块，拖动完成拼图";
      this.clearTimers();
      this.removeDocListeners();
    },

    async initCanvas() {
      try {
        const mainCanvas = this.$refs.scMain;
        if (!mainCanvas) throw new Error("main canvas not ready");
        this.mainCanvas = mainCanvas;
        this.mainCtx = mainCanvas.getContext("2d");
        this.W = mainCanvas.clientWidth || this.width;
        this.H = mainCanvas.clientHeight || this.height;
        this.mainCanvas.width = this.W * this.dpr;
        this.mainCanvas.height = this.H * this.dpr;
        this.mainCtx.scale(this.dpr, this.dpr);

        await this.loadImage();

        const pieceCanvas = this.$refs.scPiece;
        if (!pieceCanvas) throw new Error("piece canvas not ready");
        this.pieceCanvas = pieceCanvas;
        this.pieceCtx = pieceCanvas.getContext("2d");

        this.genChallenge();
      } catch (e) {
        console.log("slideCaptcha initCanvas error ==", e);
        this.tipText = "验证组件加载失败，请关闭后重试";
      }
    },

    // 底图加载：不传 image 时直接走程序化随机背景；传数组时随机取一张；加载失败退化随机背景
    loadImage() {
      return new Promise((resolve) => {
        if (this.img) {
          resolve();
          return;
        }
        let src = this.image;
        if (Array.isArray(src)) {
          src = src.length
            ? src[Math.floor(Math.random() * src.length)]
            : null;
        }
        if (!src) {
          resolve();
          return;
        }
        const img = new Image();
        img.onload = () => {
          this.img = img;
          resolve();
        };
        img.onerror = () => {
          console.log("slideCaptcha image load error ==", src);
          resolve();
        };
        img.src = src;
      });
    },

    // ---------- 背景生成与绘制 ----------

    // 程序化随机背景参数：随机值全部在这一次生成并固定到 bgParams，绘制阶段禁止再随机，
    // 否则主画布（压暗）与拼块画布（原亮度）的内容会对不齐
    genBgParams(W, H) {
      const hue = randInt(0, 360); // 主色相
      const hue2 = hue + randInt(30, 80); // 对角渐变相邻色相
      // 装饰色：色相围绕主色相 ±delta 波动，饱和度/明度随机
      const mkColor = (delta, alpha) =>
        "hsla(" +
        (hue + delta) +
        "," +
        randInt(45, 75) +
        "%," +
        randInt(45, 72) +
        "%," +
        alpha +
        ")";

      // 光晕：径向渐变，色相与主色相差 ±80，中心/半径随机
      const glow = {
        x: rand(W * 0.2, W * 0.8),
        y: rand(H * 0.2, H * 0.8),
        r: rand(Math.min(W, H) * 0.25, Math.min(W, H) * 0.5),
        hue3: hue + (Math.random() < 0.5 ? -80 : 80),
      };

      // 装饰大圆 4~6 个
      const circles = [];
      for (let i = 0, n = randInt(4, 6); i < n; i++) {
        circles.push({
          x: rand(0, W),
          y: rand(0, H),
          r: rand(14, Math.max(W, H) * 0.22),
          color: mkColor(randInt(-45, 45), rand(0.08, 0.2)),
        });
      }

      // 装饰胶囊 2~4 个（随机旋转的圆角矩形）
      const capsules = [];
      for (let i = 0, n = randInt(2, 4); i < n; i++) {
        const w = rand(W * 0.1, W * 0.28);
        const h = Math.max(6, Math.min(16, w * 0.18));
        capsules.push({
          x: rand(0, W),
          y: rand(0, H),
          w: w,
          h: h,
          angle: rand(0, Math.PI),
          color: mkColor(randInt(-45, 45), rand(0.12, 0.25)),
        });
      }

      // 装饰多边形 2~3 个（3~5 边形，顶点角度 ±0.4rad、半径 0.7~1.15 抖动，预生成到参数）
      const polygons = [];
      for (let i = 0, n = randInt(2, 3); i < n; i++) {
        const sides = randInt(3, 5);
        const pts = [];
        for (let s = 0; s < sides; s++) {
          pts.push({
            ang: (Math.PI * 2 * s) / sides + rand(-0.4, 0.4),
            rad: rand(0.7, 1.15),
          });
        }
        polygons.push({
          x: rand(0, W),
          y: rand(0, H),
          r: rand(12, Math.max(W, H) * 0.16),
          rot: rand(0, Math.PI * 2),
          pts: pts,
          color: mkColor(randInt(-45, 45), rand(0.1, 0.22)),
        });
      }

      // 小亮点 6~12 个
      const dots = [];
      for (let i = 0, n = randInt(6, 12); i < n; i++) {
        dots.push({
          x: rand(0, W),
          y: rand(0, H),
          r: rand(1.5, 3.5),
          color: mkColor(randInt(-30, 30), rand(0.2, 0.45)),
        });
      }

      return { hue, hue2, glow, circles, capsules, polygons, dots };
    },

    // 程序化背景绘制：只用 bgParams 里的固定随机值，禁止 rand*()
    drawProceduralBg(ctx, W, H, bg) {
      // 底色：对角线性渐变
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, "hsl(" + bg.hue + ",55%,68%)");
      g.addColorStop(1, "hsl(" + bg.hue2 + ",55%,58%)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);

      // 光晕：径向渐变 hsla(hue3,60%,60%,0.4) 渐隐到 0
      const rg = ctx.createRadialGradient(
        bg.glow.x,
        bg.glow.y,
        0,
        bg.glow.x,
        bg.glow.y,
        bg.glow.r
      );
      rg.addColorStop(0, "hsla(" + bg.glow.hue3 + ",60%,60%,0.4)");
      rg.addColorStop(1, "hsla(" + bg.glow.hue3 + ",60%,60%,0)");
      ctx.fillStyle = rg;
      ctx.fillRect(0, 0, W, H);

      // 装饰大圆
      bg.circles.forEach((c) => {
        ctx.fillStyle = c.color;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // 装饰胶囊
      bg.capsules.forEach((c) => {
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.angle);
        ctx.fillStyle = c.color;
        roundRectPath(ctx, -c.w / 2, -c.h / 2, c.w, c.h, c.h / 2);
        ctx.fill();
        ctx.restore();
      });

      // 装饰多边形
      bg.polygons.forEach((p) => {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.beginPath();
        p.pts.forEach((pt, i) => {
          const x = Math.cos(pt.ang) * p.r * pt.rad;
          const y = Math.sin(pt.ang) * p.r * pt.rad;
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.closePath();
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.restore();
      });

      // 小亮点
      bg.dots.forEach((d) => {
        ctx.fillStyle = d.color;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
      });
    },

    drawBackground(ctx, W, H, bg, dim) {
      if (this.img) {
        // 图片按 cover 裁切铺满
        const iw = this.img.width;
        const ih = this.img.height;
        if (iw && ih) {
          const scale = Math.max(W / iw, H / ih);
          const dw = iw * scale;
          const dh = ih * scale;
          ctx.drawImage(this.img, (W - dw) / 2, (H - dh) / 2, dw, dh);
        } else {
          ctx.drawImage(this.img, 0, 0, W, H);
        }
      } else {
        // 无图：程序化随机背景
        this.drawProceduralBg(ctx, W, H, bg);
      }
      // dim=true 时整体压暗（主画布用）：暗底亮块，拼块与背景拉开层次
      if (dim) {
        ctx.fillStyle = "rgba(0,0,0,0.35)";
        ctx.fillRect(0, 0, W, H);
      }
    },

    // ---------- 挑战生成与画布绘制 ----------

    // 随机拼块形状：1~2 对凸起+凹口随机落在四条边上，其余边平直
    genShape() {
      const sides = ["top", "right", "bottom", "left"];
      const n = randInt(1, 2); // 凸起/凹口对数
      const feats = [];
      for (let i = 0; i < n; i++) feats.push(1);
      for (let i = 0; i < n; i++) feats.push(-1);
      while (feats.length < 4) feats.push(0);
      for (let i = feats.length - 1; i > 0; i--) {
        const j = randInt(0, i);
        const t = feats[i];
        feats[i] = feats[j];
        feats[j] = t;
      }
      const f = {};
      sides.forEach((s, i) => {
        f[s] = feats[i];
      });
      return f;
    },

    genChallenge() {
      const W = this.W;
      const H = this.H;
      if (W < 60 || H < 60) {
        throw new Error("canvas size invalid: " + W + "x" + H);
      }
      // 拼块单位长度：方体 3e×3e，宽高一致（310 宽画布上 e=15，方体 45×45）
      const e = Math.max(10, Math.min(18, Math.round((W * 15) / 310)));
      const shape = this.genShape();
      const leftB = shape.left === 1 ? e / 2 : 0;
      const rightB = shape.right === 1 ? e / 2 : 0;
      const topB = shape.top === 1 ? e / 2 : 0;
      const bottomB = shape.bottom === 1 ? e / 2 : 0;
      const pad = 6;
      const offMin = leftB; // 左凸时拼块起始位置让出凸起宽度
      const offMax = W - 3 * e - rightB - pad;
      const minX = offMin + pad;
      const maxX = offMax - pad;
      const minY = topB + pad;
      const maxY = H - 3 * e - bottomB - pad;
      if (maxX - minX < 20 || maxY - minY < 10) {
        throw new Error("canvas too small for puzzle");
      }
      this.e = e;
      this.shape = shape;
      this.holeX = Math.round(rand(minX, maxX));
      this.holeY = Math.round(rand(minY, maxY));
      this.offMin = Math.round(offMin);
      this.offMax = Math.round(offMax);
      this.offset = this.offMin;
      this.bgParams = this.genBgParams(W, H);
      this.drawMain(true);
      this.drawPiece();
      this.ready = true;
    },

    drawMain(withHole) {
      const ctx = this.mainCtx;
      if (!ctx || !this.bgParams || !this.e || !this.shape) return;
      ctx.clearRect(0, 0, this.W, this.H);
      // 主画布背景压暗（dim），拼块保持原亮度，拉开层次
      this.drawBackground(ctx, this.W, this.H, this.bgParams, true);
      if (withHole) {
        // 缺口：白色磨砂 + 轻微内阴影 + 白描边（vcode 风格）
        ctx.save();
        traceBrickPath(ctx, this.holeX, this.holeY, this.e, this.shape);
        ctx.fillStyle = "rgba(255,255,255,0.8)";
        ctx.fill();
        ctx.clip();
        ctx.shadowColor = "rgba(0,0,0,0.3)";
        ctx.shadowBlur = 3;
        ctx.strokeStyle = "rgba(0,0,0,0.3)";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
        ctx.save();
        traceBrickPath(ctx, this.holeX, this.holeY, this.e, this.shape);
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
    },

    drawPiece() {
      const ctx = this.pieceCtx;
      if (!ctx || !this.bgParams || !this.e || !this.shape) return;
      const m = this.pieceMargin;
      const e = this.e;
      const shape = this.shape;
      const leftB = shape.left === 1 ? e / 2 : 0;
      const rightB = shape.right === 1 ? e / 2 : 0;
      const topB = shape.top === 1 ? e / 2 : 0;
      const bottomB = shape.bottom === 1 ? e / 2 : 0;
      // 画布包围盒：方体 3e×3e + 各边凸起 + 留白
      const cw = 3 * e + leftB + rightB + m * 2;
      const ch = 3 * e + topB + bottomB + m * 2;
      this.pieceCanvas.width = cw * this.dpr;
      this.pieceCanvas.height = ch * this.dpr;
      ctx.scale(this.dpr, this.dpr);
      // 内容原点：方体左上角在画布 (leftB + m, topB + m)，
      // 故 translate = (m - holeX + leftB, m - holeY + topB)，bgX/bgY 不含 -m
      const bgX = this.holeX - leftB;
      const bgY = this.holeY - topB;
      // 拼块内容 = 缺口处同位置图片裁切（不压暗，保持亮色）
      ctx.save();
      ctx.translate(m - bgX, m - bgY);
      traceBrickPath(ctx, this.holeX, this.holeY, e, shape);
      ctx.clip();
      this.drawBackground(ctx, this.W, this.H, this.bgParams, false);
      ctx.restore();
      // 白描边 + 轻投影
      ctx.save();
      ctx.translate(m - bgX, m - bgY);
      traceBrickPath(ctx, this.holeX, this.holeY, e, shape);
      ctx.shadowColor = "rgba(0,0,0,0.45)";
      ctx.shadowBlur = 4;
      ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.restore();
      // 更新 CSS 尺寸与定位（方体左上角跟随 offset，与缺口精确对齐）
      this.pieceCssW = cw;
      this.pieceCssH = ch;
      this.pieceTop = this.holeY - topB - m;
      this.pieceLeft = this.offset - leftB - m;
    },

    // ---------- 拖动与校验 ----------

    onClose() {
      if (this.dragging) return;
      this.$emit("close");
    },
    onStart(e) {
      if (this.passed || this.verifying || this.dragging || !this.ready) return;
      if (e.preventDefault) e.preventDefault();
      // 失败复位定时器未触发前再次拖动：取消复位
      if (this._failTimer) {
        clearTimeout(this._failTimer);
        this._failTimer = null;
      }
      this.dragging = true;
      this.failShake = false;
      this.startX = e.clientX;
      this.baseOffset = this.offset;
      // 优先 pointer capture；老浏览器退回 document 鼠标监听
      const track = this.$refs.scTrack;
      if (track && track.setPointerCapture && typeof e.pointerId === "number") {
        try {
          track.setPointerCapture(e.pointerId);
        } catch (err) {
          console.log("slideCaptcha setPointerCapture error ==", err);
        }
      } else {
        this._docListeners = true;
        document.addEventListener("mousemove", this.onMove);
        document.addEventListener("mouseup", this.onEnd);
      }
    },
    onMove(e) {
      if (!this.dragging || this.passed) return;
      if (e.preventDefault) e.preventDefault();
      const dx = e.clientX - this.startX;
      let next = this.baseOffset + dx;
      if (next < this.offMin) next = this.offMin;
      if (next > this.offMax) next = this.offMax;
      this.offset = next;
      const leftB = this.shape && this.shape.left === 1 ? this.e / 2 : 0;
      this.pieceLeft = next - leftB - this.pieceMargin;
    },
    onEnd() {
      if (!this.dragging) return;
      this.removeDocListeners();
      this.dragging = false;
      if (Math.abs(this.offset - this.holeX) <= this.tolerance) {
        // 通过：拼块吸附缺口 -> 补全缺口 -> 通知父组件
        this.verifying = true;
        this.offset = this.holeX;
        const leftB = this.shape && this.shape.left === 1 ? this.e / 2 : 0;
        this.pieceLeft = this.holeX - leftB - this.pieceMargin;
        this.tipText = "验证通过";
        this._passTimer = setTimeout(() => {
          this.passed = true;
          this.drawMain(false);
          this._emitTimer = setTimeout(() => {
            this.$emit("success");
          }, 300);
        }, 260);
      } else {
        // 失败：抖动提示，随后复位并重新生成挑战
        this.failShake = true;
        this.tipText = "验证失败，请重试";
        this._failTimer = setTimeout(() => {
          this.offset = this.offMin;
          this.failShake = false;
          this.tipText = "按住滑块，拖动完成拼图";
          this.genChallenge();
        }, 500);
      }
    },
  },
};
</script>

<style scoped>
.sc-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  -webkit-user-select: none;
  user-select: none;
}

.sc-box {
  background-color: #ffffff;
  border-radius: 6px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}

.sc-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.sc-title {
  color: #3c3c3c;
  font-size: 15px;
  font-weight: 500;
}

.sc-close {
  width: 24px;
  height: 24px;
  line-height: 22px;
  text-align: center;
  color: #999999;
  font-size: 22px;
  cursor: pointer;
}

.sc-puzzle {
  position: relative;
  overflow: hidden;
  background-color: #f2f3f5;
  border-radius: 4px;
}

.sc-canvas {
  display: block;
}

.sc-piece-canvas {
  position: absolute;
  transition: left 0.18s ease;
}

.sc-piece-canvas.dragging {
  transition: none;
}

.shaking {
  animation: scShake 0.3s;
}

@keyframes scShake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}

.sc-ok {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #07c160;
  font-size: 60px;
  font-weight: 600;
  z-index: 3;
}

.sc-bar {
  margin-top: 14px;
}

/* vcode 风格拖动条：白色轨道 + 蓝色进度 + 白色把手 */
.sc-track {
  position: relative;
  height: 44px;
  background-color: #eef1f8;
  border-radius: 4px;
  display: flex;
  align-items: center;
  overflow: hidden;
  box-shadow: 0 0 4px rgba(240, 240, 240, 0.6) inset;
  touch-action: none;
}

.sc-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(1, 173, 231, 0.35);
  transition: none;
}

.sc-fill.ok {
  background-color: rgba(7, 193, 96, 0.35);
}

.sc-tip {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  color: #9aa0ae;
  font-size: 13px;
}

.sc-handle {
  position: absolute;
  left: 0;
  top: 0;
  width: 44px;
  height: 44px;
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.18s ease;
  cursor: pointer;
}

.sc-handle.ok {
  background-color: #07c160;
}

.sc-handle.dragging {
  transition: none;
}

/* 把手三条竖杠图标 */
.sc-bars {
  display: flex;
  align-items: center;
  height: 40%;
}

.sc-bar-line {
  width: 2px;
  height: 100%;
  background-color: #01ade7;
  border-radius: 1px;
  margin: 0 2px;
}

.sc-handle-icon {
  color: #ffffff;
  font-size: 20px;
}
</style>
